import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Users, Building, QrCode, AlertTriangle, RefreshCw, Loader2,
  UserPlus, Plus, FileText, Eye
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [recentIncidents, setRecentIncidents] = useState([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsData, scansData, incidentsData] = await Promise.all([
        api.getDashboardStats(),
        api.getScans({ limit: 5 }),
        api.getIncidents(),
      ]);
      setStats(statsData);
      setRecentScans(scansData);
      setRecentIncidents(incidentsData.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getSeverityColor = (severity) => {
    const colors = {
      low: 'bg-green-100 text-green-700',
      medium: 'bg-amber-100 text-amber-700',
      high: 'bg-orange-100 text-orange-700',
      critical: 'bg-red-100 text-red-700',
    };
    return colors[severity] || 'bg-slate-100 text-slate-700';
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-amber-100 text-amber-700',
      investigating: 'bg-blue-100 text-blue-700',
      resolved: 'bg-green-100 text-green-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-500">Welcome back,</p>
            <h1 className="text-2xl font-bold text-slate-800">{user?.name}</h1>
          </div>
          <Button onClick={loadData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} title="Total Guards" value={stats?.total_guards || 0} color="blue" />
          <StatCard icon={Building} title="Active Sites" value={stats?.total_sites || 0} color="green" />
          <StatCard icon={QrCode} title="Scans Today" value={stats?.scans_today || 0} color="sky" />
          <StatCard icon={AlertTriangle} title="Open Incidents" value={stats?.open_incidents || 0} color="amber" />
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <ActionCard icon={UserPlus} label="Add Guard" onClick={() => navigate('/admin/users')} color="blue" />
            <ActionCard icon={Plus} label="Add Site" onClick={() => navigate('/admin/sites')} color="green" />
            <ActionCard icon={FileText} label="View Reports" onClick={() => navigate('/admin/reports')} color="sky" />
            <ActionCard icon={Eye} label="View Incidents" onClick={() => navigate('/admin/incidents')} color="amber" />
          </div>
        </div>

        {/* Tables Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Scans */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Recent Scans</CardTitle>
              <Button variant="link" className="text-blue-700 p-0" onClick={() => navigate('/admin/patrol')}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                {recentScans.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No recent scans</p>
                ) : (
                  recentScans.map((scan) => (
                    <div key={scan.id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-800">{scan.checkpoint_name}</p>
                        <p className="text-sm text-slate-500">{scan.guard_name} • {scan.site_name}</p>
                      </div>
                      <span className="text-sm text-slate-500">
                        {format(new Date(scan.scanned_at), 'h:mm a')}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Incidents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Recent Incidents</CardTitle>
              <Button variant="link" className="text-blue-700 p-0" onClick={() => navigate('/admin/incidents')}>
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                {recentIncidents.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No recent incidents</p>
                ) : (
                  recentIncidents.map((incident) => (
                    <div key={incident.id} className="py-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-slate-800">{incident.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityColor(incident.severity)}`}>
                          {incident.severity}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(incident.status)}`}>
                          {incident.status}
                        </span>
                        <span className="text-sm text-slate-500">
                          {format(new Date(incident.created_at), 'MMM d, h:mm a')}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

function StatCard({ icon: Icon, title, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    sky: 'bg-sky-50 text-sky-700',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
          <p className="text-sm text-slate-500">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ActionCard({ icon: Icon, label, onClick, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    sky: 'bg-sky-50 text-sky-700',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <button
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-3 hover:border-blue-300 transition-colors"
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-sm font-medium text-slate-700">{label}</span>
    </button>
  );
}